import DialogFooter from 'components/actions/DialogFooter';
import Dialog from 'components/overlays/Dialog';
import type { UserFE } from 'domain/systemAdmin';
import { SystemAdminDialogTypes } from 'helper/types';
import { useSelector } from 'react-redux';
import { getActiveUser, getSystemAdminOpenDialog } from 'store/slices/settingsPageSlice';

import styles from './styles/DeleteUserDialog.module.scss';
import { getUserFullName } from './utils';

interface TitleProps {
    user?: UserFE;
}

const Title = ({ user }: TitleProps) => {
    const userName = getUserFullName(user) ?? 'this user';

    return (
        <p className={styles.title}>
            Are you sure you want to delete <span className={styles.bold}>{userName}</span>&apos;s account?
        </p>
    );
};

const Body = ({ user }: TitleProps) => {
    const userName = getUserFullName(user) ?? 'this user';

    return (
        <div className='display-flex flex-column align-items-center'>
            <span className={styles.icon} aria-hidden='true'>
                <span className={`rioglyph rioglyph-trash ${styles.deleteIcon}`} />
                <span className={`rioglyph rioglyph-user ${styles.userIcon}`} />
            </span>
            <p className={styles.body}>
                This action will permanently remove <span className={styles.bold}>{userName}</span>&apos;s account and
                revoke all access. It cannot be undone.
            </p>
        </div>
    );
};

interface Props {
    onConfirm: () => void;
    onCancel: () => void;
    isLoading?: boolean;
}

const DeleteUserDialog = ({ onConfirm, onCancel, isLoading }: Props) => {
    const systemAdminOpenDialog = useSelector(getSystemAdminOpenDialog);
    const show = systemAdminOpenDialog === SystemAdminDialogTypes.DELETE_USER_DIALOG;
    const activeUser = useSelector(getActiveUser);

    return (
        <Dialog
            data-testid='system-users-dialog-delete-user'
            show={show}
            title={<Title user={activeUser} />}
            body={<Body user={activeUser} />}
            footer={
                <DialogFooter
                    id='delete-user'
                    submitLabel='Yes, delete'
                    loadingSubmitLabel='Deleting'
                    submitIcon={<span className='rioglyph rioglyph-trash mr-5 align-middle text-16' aria-hidden />}
                    onSubmit={onConfirm}
                    cancelLabel='No, cancel'
                    onCancel={onCancel}
                    style='danger'
                    isLoading={isLoading}
                />
            }
            size='sm'
            onClose={onCancel}
            showCloseButton
        />
    );
};

export default DeleteUserDialog;
